// src/utils/toastTestUtils.ts
// Utility functions and constants for toast testing

export interface TimingPreset {
  name: string;
  description: string;
  scaleInDuration: number;
  iconDrawDuration: number;
  iconDrawDelay: number;
  holdDuration: number;
  fadeOutDuration: number;
}

export interface TimingConfig {
  scaleInDuration: number;
  iconDrawDuration: number;
  iconDrawDelay: number;
  holdDuration: number;
  fadeOutDuration: number;
}

// Timing presets for quick testing
export const TIMING_PRESETS: TimingPreset[] = [
  {
    name: 'Current/Default',
    description: 'Current production timing',
    scaleInDuration: 0.5,
    iconDrawDuration: 0.3,
    iconDrawDelay: 0.4,
    holdDuration: 0.5,
    fadeOutDuration: 0.3,
  },
  {
    name: 'Snappy & Professional',
    description: 'Fast and responsive (1.8s total)',
    scaleInDuration: 0.4,
    iconDrawDuration: 0.4,
    iconDrawDelay: 0.3,
    holdDuration: 0.7,
    fadeOutDuration: 0.3,
  },
  {
    name: 'Smooth & Confident',
    description: 'Elegant and comfortable (2.2s total)',
    scaleInDuration: 0.6,
    iconDrawDuration: 0.5,
    iconDrawDelay: 0.4,
    holdDuration: 0.8,
    fadeOutDuration: 0.3,
  },
  {
    name: 'Quick & Efficient',
    description: 'Very fast confirmation (1.3s total)',
    scaleInDuration: 0.3,
    iconDrawDuration: 0.25,
    iconDrawDelay: 0.2,
    holdDuration: 0.4,
    fadeOutDuration: 0.2,
  },
  {
    name: 'Slow Motion',
    description: 'For detailed analysis (6s total)',
    scaleInDuration: 1.5,
    iconDrawDuration: 1.0,
    iconDrawDelay: 1.0,
    holdDuration: 2.0,
    fadeOutDuration: 0.5,
  },
];

// Sample messages for testing
export const SAMPLE_MESSAGES = [
  'Saved!',
  'Pet added successfully!',
  'Booking confirmed!',
  'Settings saved!',
  'Client created successfully!',
  'Changes saved!',
  'Data uploaded!',
  'Profile updated!',
  'Appointment scheduled!',
  'Payment processed!',
];

// Error messages for testing fail toast
export const SAMPLE_ERROR_MESSAGES = [
  'Operation failed!',
  'Failed to save pet!',
  'Booking error!',
  'Settings not saved!',
  'Client creation failed!',
  'Changes not saved!',
  'Upload failed!',
  'Profile update error!',
  'Scheduling failed!',
  'Payment error!',
];

// Calculate total animation time
export const calculateTotalTime = (timing: TimingConfig): number => {
  const {
    scaleInDuration,
    iconDrawDuration,
    iconDrawDelay,
    holdDuration,
    fadeOutDuration,
  } = timing;

  // Total time = scale in + (icon draw delay + icon draw duration) + hold + fade out
  // But icon draw starts during scale in, so we take the max of those overlapping phases
  const mainPhase = Math.max(scaleInDuration, iconDrawDelay + iconDrawDuration);
  return mainPhase + holdDuration + fadeOutDuration;
};

// Format time in milliseconds for display
export const formatTime = (seconds: number): string => {
  const ms = Math.round(seconds * 1000);
  return `${ms}ms`;
};

// Format total time with breakdown
export const formatTimeBreakdown = (timing: TimingConfig): string => {
  const total = calculateTotalTime(timing);
  return `${formatTime(total)} total (Scale: ${formatTime(timing.scaleInDuration)}, Draw: ${formatTime(timing.iconDrawDuration)}, Hold: ${formatTime(timing.holdDuration)}, Fade: ${formatTime(timing.fadeOutDuration)})`;
};

// Easing functions reference (for documentation)
export const EASING_FUNCTIONS = {
  'cubic-bezier(0.68, -0.55, 0.265, 1.55)': 'Back (bounce effect)',
  'ease-out': 'Deceleration',
  'ease-in': 'Acceleration',
  'ease-in-out': 'Smooth start and end',
  linear: 'Constant speed',
  'cubic-bezier(0.25, 0.46, 0.45, 0.94)': 'Smooth',
};

// Get timing preset by name
export const getPresetByName = (name: string): TimingPreset | undefined => {
  return TIMING_PRESETS.find((preset) => preset.name === name);
};

// Convert timing config to CSS animation string
export const generateAnimationString = (
  keyframeName: string,
  duration: number,
  delay: number = 0,
  easing: string = 'ease-out'
): string => {
  if (delay > 0) {
    return `${keyframeName} ${duration}s ${easing} ${delay}s forwards`;
  }
  return `${keyframeName} ${duration}s ${easing} forwards`;
};

// Validate timing values
export const validateTiming = (timing: TimingConfig): boolean => {
  const { scaleInDuration, iconDrawDuration, holdDuration, fadeOutDuration } =
    timing;

  // All values must be positive
  if (
    scaleInDuration <= 0 ||
    iconDrawDuration <= 0 ||
    holdDuration < 0 ||
    fadeOutDuration <= 0
  ) {
    return false;
  }

  // Reasonable maximum (10 seconds total)
  const total = calculateTotalTime(timing);
  if (total > 10) {
    return false;
  }

  return true;
};
